import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute, OnboardingRoute } from "@/components/auth/ProtectedRoute";
import { LegacyRedirect } from "@/components/routing/LegacyRedirect";
import ErrorBoundary from "@/components/ErrorBoundary";
import { RequireNSMember } from "@/components/auth/RequireNSMember";
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));
import { BrandedLoader } from "@/components/BrandedLoader";

// Lazy-loaded pages for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Splash = lazy(() => import("./pages/Splash"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Home = lazy(() => import("./pages/Home"));
const PracticeHub = lazy(() => import("./pages/PracticeHub"));
const Practice = lazy(() => import("./pages/Practice"));
const EveningPractice = lazy(() => import("./pages/EveningPractice"));
const MiddayPractice = lazy(() => import("./pages/MiddayPractice"));
const Archive = lazy(() => import("./pages/Archive"));
const Journey = lazy(() => import("./pages/Journey"));
const Settings = lazy(() => import("./pages/Settings"));
const Academy = lazy(() => import("./pages/Academy"));
const ModuleDetail = lazy(() => import("./pages/ModuleDetail"));
const AcademyLessonPage = lazy(() => import("./pages/AcademyLesson"));
const ModuleComplete = lazy(() => import("./pages/ModuleComplete"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AlphaFeedback = lazy(() => import("./pages/AlphaFeedback"));
const VoiceLab = lazy(() => import("./pages/VoiceLab"));
const AlphaGuide = lazy(() => import("./pages/AlphaGuide"));
const DemoLogin = lazy(() => import("./pages/DemoLogin"));
const NSLanding = lazy(() => import("./pages/NSLanding"));
const NSOnboardingRedirect = lazy(() => import("./pages/NSOnboardingRedirect"));
const NSDebrief = lazy(() => import("./pages/NSDebrief").then(m => ({ default: m.NSDebrief })));
const NSCircle = lazy(() => import("./pages/NSCircle"));
const NSCircleGuidelines = lazy(() => import("./pages/NSCircleGuidelines"));

// Admin imports (lazy)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAcademy = lazy(() => import("./pages/admin/AdminAcademy"));
const AdminPrompts = lazy(() => import("./pages/admin/AdminPrompts"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminAlphaFeedback = lazy(() => import("./pages/admin/AdminAlphaFeedback"));
const AdminVoiceLab = lazy(() => import("./pages/admin/AdminVoiceLab"));
const InAppFeedback = lazy(() => import("./pages/admin/InAppFeedback"));
const AdminNSCircle = lazy(() => import("./pages/admin/AdminNSCircle"));

import { AdminProtectedRoute } from "@/components/admin/AdminProtectedRoute";
const AnalyticsProvider = lazy(() =>
  import("@/components/analytics/PostHogProvider").then(m => ({ default: m.AnalyticsProvider }))
);

// Tuned defaults: cache lingers across nav so revisits feel instant; refetch
// silently in the background to keep data fresh. Retries are bounded so
// transient failures don't spin forever.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

const App = () => {
  // NS referral detection — capture ?ref=ns from any URL on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('ref') === 'ns') {
        localStorage.setItem('thnx_ref', 'ns');
        localStorage.setItem('thnx_ns_cohort', 'NS2026');
      }
    } catch { /* ignore */ }
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <Suspense fallback={<>{/* analytics loads lazily */}</>}>
        <AnalyticsProvider>
          <ErrorBoundary>
            <Toaster />
            <Sonner />
            <Routes>
              {/* Public routes — no branded loader */}
              <Route path="/" element={<Suspense fallback={null}><Landing /></Suspense>} />
              <Route path="/splash" element={<Suspense fallback={null}><Splash /></Suspense>} />
              <Route path="/auth" element={<Suspense fallback={null}><Auth /></Suspense>} />
              <Route path="/auth/callback" element={<Suspense fallback={<BrandedLoader variant="fullscreen" />}><AuthCallback /></Suspense>} />
              <Route path="/reset-password" element={<Suspense fallback={<BrandedLoader variant="fullscreen" />}><ResetPassword /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={null}><Privacy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={null}><Terms /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={null}><Contact /></Suspense>} />
              <Route path="/auth/demo" element={<Suspense fallback={null}><DemoLogin /></Suspense>} />
              <Route path="/alpha-feedback" element={<Suspense fallback={null}><AlphaFeedback /></Suspense>} />
              <Route path="/alpha" element={<Suspense fallback={null}><AlphaGuide /></Suspense>} />
              <Route path="/ns" element={<Suspense fallback={null}><NSLanding /></Suspense>} />
              <Route path="/ns/onboarding" element={<Suspense fallback={<BrandedLoader variant="fullscreen" />}><NSOnboardingRedirect /></Suspense>} />
              
              {/* Onboarding route (special handling) */}
              <Route path="/app/onboarding" element={
                <Suspense fallback={null}>
                  <OnboardingRoute>
                    <Onboarding />
                  </OnboardingRoute>
                </Suspense>
              } />
              
              {/* Protected app routes — branded loader for in-app navigation */}
              <Route path="/app" element={
                <Suspense fallback={<BrandedLoader variant="fullscreen" />}>
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                </Suspense>
              }>
                <Route path="home" element={<ErrorBoundary context="Home"><Home /></ErrorBoundary>} />
                <Route path="practice" element={<ErrorBoundary context="PracticeHub"><PracticeHub /></ErrorBoundary>} />
                <Route path="daily-gratitude" element={<ErrorBoundary context="Practice"><Practice /></ErrorBoundary>} />
                <Route path="evening-practice" element={<ErrorBoundary context="EveningPractice"><EveningPractice /></ErrorBoundary>} />
                <Route path="midday-practice" element={<ErrorBoundary context="MiddayPractice"><MiddayPractice /></ErrorBoundary>} />
                <Route path="archive" element={<ErrorBoundary context="Archive"><Archive /></ErrorBoundary>} />
                <Route path="journey" element={<ErrorBoundary context="Journey"><Journey /></ErrorBoundary>} />
                <Route path="settings" element={<ErrorBoundary context="Settings"><Settings /></ErrorBoundary>} />
                <Route path="academy" element={<ErrorBoundary context="Academy"><Academy /></ErrorBoundary>} />
                <Route path="academy/module/:moduleId" element={<ErrorBoundary context="ModuleDetail"><ModuleDetail /></ErrorBoundary>} />
                <Route path="academy/lesson/:lessonId" element={<ErrorBoundary context="AcademyLesson"><AcademyLessonPage /></ErrorBoundary>} />
                <Route path="academy/module/:moduleId/complete" element={<ErrorBoundary context="ModuleComplete"><ModuleComplete /></ErrorBoundary>} />
                <Route path="voice-lab" element={<ErrorBoundary context="VoiceLab"><VoiceLab /></ErrorBoundary>} />
                <Route path="ns-debrief" element={<RequireNSMember><ErrorBoundary context="NSDebrief"><NSDebrief /></ErrorBoundary></RequireNSMember>} />
                <Route path="circle/ns-circle" element={<RequireNSMember><ErrorBoundary context="NSCircle"><NSCircle /></ErrorBoundary></RequireNSMember>} />
                <Route path="circle/guidelines" element={<ErrorBoundary context="NSCircleGuidelines"><NSCircleGuidelines /></ErrorBoundary>} />
              </Route>
              
              {/* Legacy route redirects */}
              <Route path="/home" element={<Navigate to="/app/home" replace />} />
              <Route path="/practice" element={<Navigate to="/app/practice" replace />} />
              <Route path="/evening-practice" element={<Navigate to="/app/evening-practice" replace />} />
              <Route path="/midday-practice" element={<Navigate to="/app/midday-practice" replace />} />
              <Route path="/archive" element={<Navigate to="/app/archive" replace />} />
              <Route path="/journey" element={<Navigate to="/app/journey" replace />} />
              <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
              <Route path="/academy" element={<Navigate to="/app/academy" replace />} />
              <Route path="/academy/module/:moduleId" element={<LegacyRedirect to="/app/academy/module/:moduleId" />} />
              <Route path="/academy/lesson/:lessonId" element={<LegacyRedirect to="/app/academy/lesson/:lessonId" />} />
              <Route path="/academy/module/:moduleId/complete" element={<LegacyRedirect to="/app/academy/module/:moduleId/complete" />} />
              <Route path="/onboarding" element={<Navigate to="/app/onboarding" replace />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<Suspense fallback={null}><AdminLogin /></Suspense>} />
              <Route path="/admin/dashboard" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/users" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute requiredPermission="users">
                    <AdminUsers />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/subscriptions" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute requiredPermission="subscriptions">
                    <AdminSubscriptions />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/analytics" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminAnalytics />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/settings" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute requiredPermission="settings">
                    <AdminSettings />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/gift-access" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute requiredPermission="subscriptions">
                    <AdminSubscriptions />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/ns-circle" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute requiredPermission="users">
                    <AdminNSCircle />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/academy" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminAcademy />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/prompts" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminPrompts />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/emails" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminEmails />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/alpha-feedback" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminAlphaFeedback />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/voice-lab" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <AdminVoiceLab />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin/in-app-feedback" element={
                <Suspense fallback={null}>
                  <AdminProtectedRoute>
                    <InAppFeedback />
                  </AdminProtectedRoute>
                </Suspense>
              } />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              
              {/* 404 */}
              <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
            </Routes>
          </ErrorBoundary>
        </AnalyticsProvider>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
